import { stampFootprint, type PropFootprint } from "../compile/footprint.ts";
import type { MapDocument } from "../document/types.ts";
import { artStrip, isFlat } from "./scatter-base.ts";
import { frameKey, type FrameLookups, type ScatterKit, type ScatterShape } from "./scatter-entries.ts";
import type { PropRef } from "./templates.ts";

/**
 * What a stroke of the prop brush must keep clear (ADR-123 decision 2.2 and the amendment of 2026-10-13, A1a): a
 * bitmap of the bases of the props, one count a cell over a whole-tile box that grows as the stroke moves, and a grid
 * of foot points with their gaps over the whole map. A prop's base is its footprint cells, or the strip of its art,
 * and its foot cell (`scatter-base.ts`); every prop of every prop layer counts, hidden or locked, since the
 * document, not the view, decides. Known ground cover (A2) is the exception: cover from the document paints no
 * base, and the stroke's own cover paints its foot cell alone, into a second bitmap, `coverFoot`, that only solid
 * placements are tested against, so a trunk is never laid over grass the stroke laid and grass never on a trunk, while
 * cover against cover is ruled by the gap alone. A set that clears ground cover (A3) also keeps, for every cover prop
 * of the document on a layer it may clear, the cell holding its foot point in a list a cell, so what a placement
 * stands on is found by reading the lists of its base cells and never by scanning the map's props; a cleared prop
 * gets a flag that the gap and the count of the set's props read, and the flags go on a stack so a preview puts the
 * ground back. Every placement of the stroke is entered in both before the next candidate is
 * tried, its base added to the bitmap as a count so that a set that may stand on other props can be taken back
 * out exactly. It is plain data, held by one stroke or one preview and changed only through the functions here.
 */

/**
 * A prop already on the map that has a base: the footprint (or strip) that blocks its cells, if it has one, its
 * placement, its foot point, whose cell is always in the base, and the box of the cells in pixels, which says whether a
 * box of the bitmap needs it stamped.
 */
interface StandingProp {
  readonly footprint: PropFootprint | undefined;
  readonly solid: boolean;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly footX: number;
  readonly footY: number;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * A placement of the stroke: its shape and its laying corner in cells.
 */
interface PlacedShape {
  readonly shape: ScatterShape;
  readonly kx: number;
  readonly ky: number;
}

/**
 * The occupancy. The box is in cells, its corners on tile edges; `cells` holds its bitmap row by row, how many bases
 * cover each cell. A foot point
 * is an index into the four `feet` arrays, filed in the bucket of `bucketPx` square pixels that holds it, a bucket
 * as wide as the set's largest gap, so every foot point within reach of a candidate is in the nine buckets around
 * it. `feetMember` is 1 for a prop of the set, 2 for a prop of known cover that is not of the set, else 0.
 * `coverFoot` holds, over the same box as `cells`, how many placements of the stroke's own cover have their foot cell
 * in each cell. `feetGone` is 1 for a foot point whose cover a placement of the stroke has cleared. When `clears`,
 * the cover of the document that may be cleared is a record each: its foot point's index (`clearFoot`), its cell
 * (`clearCol`, `clearRow`) and the layer and prop it is (`clearLayer`, `clearProp`); `clearHead` holds the first
 * record of each cell of the box, or -1, and `clearNext` the next of the same cell. `gone` is the stack of records
 * cleared, `scratch` the records a candidate would clear, reused. `refusals` counts, for the dab running, the tries
 * each rule refused: the map's edge, other props, the gap and the ground, in that order (A4.1).
 */
export interface ScatterOccupancy {
  readonly tileSize: number;
  readonly cellsPerTile: number;
  readonly cellPx: number;
  readonly widthCells: number;
  readonly heightCells: number;
  boxLeft: number;
  boxTop: number;
  boxWidth: number;
  boxHeight: number;
  cells: Uint16Array;
  coverFoot: Uint16Array;
  readonly bucketPx: number;
  readonly bucketsAcross: number;
  readonly bucketsDown: number;
  readonly buckets: Map<number, number[]>;
  readonly feetX: number[];
  readonly feetY: number[];
  readonly feetGap: number[];
  readonly feetMember: number[];
  readonly feetGone: number[];
  readonly standing: readonly StandingProp[];
  readonly placed: PlacedShape[];
  readonly clears: boolean;
  clearFoot: Int32Array;
  clearCol: Int32Array;
  clearRow: Int32Array;
  clearLayer: string[];
  clearProp: string[];
  clearHead: Int32Array;
  clearNext: Int32Array;
  readonly gone: number[];
  readonly scratch: number[];
  readonly refusals: Uint32Array;
}

/**
 * A point an occupancy can be put back to: how many foot points and placements it held, and how many records of
 * cover were cleared.
 */
export interface OccupancyMark {
  readonly feet: number;
  readonly placed: number;
  readonly gone: number;
}

/**
 * Tiles of room the bitmap keeps around a box it grows to, so a stroke dragged a little further does not grow it
 * again at once.
 */
const GROW_TILES = 8;

/**
 * Files a foot point.
 *
 * @param occupancy The occupancy.
 * @param x The foot point's x in pixels.
 * @param y The foot point's y in pixels.
 * @param gap The gap its prop keeps.
 * @param member 1 for a prop of the set, 2 for one of known cover that is not, else 0.
 */
function addFoot(occupancy: ScatterOccupancy, x: number, y: number, gap: number, member: number): void {
  const index = occupancy.feetX.length;
  occupancy.feetX.push(x);
  occupancy.feetY.push(y);
  occupancy.feetGap.push(gap);
  occupancy.feetMember.push(member);
  occupancy.feetGone.push(0);
  const key = bucketKey(occupancy, x, y);
  const bucket = occupancy.buckets.get(key);
  if (bucket === undefined) {
    occupancy.buckets.set(key, [index]);
  } else {
    bucket.push(index);
  }
}

/**
 * The bucket holding a point, a point off the map filed in the nearest edge bucket, which only costs a distance
 * test and never hides a point within reach.
 *
 * @param occupancy The occupancy.
 * @param x The point's x in pixels.
 * @param y The point's y in pixels.
 * @returns The bucket's key.
 */
function bucketKey(occupancy: ScatterOccupancy, x: number, y: number): number {
  const bx = Math.min(occupancy.bucketsAcross - 1, Math.max(0, Math.floor(x / occupancy.bucketPx)));
  const by = Math.min(occupancy.bucketsDown - 1, Math.max(0, Math.floor(y / occupancy.bucketPx)));
  return by * occupancy.bucketsAcross + bx;
}

/**
 * No prop ids: the default of the props that are kept standing.
 */
const NO_PROPS: ReadonlySet<string> = new Set();

/**
 * Builds a stroke's occupancy over a map for a prepared set: every prop of every prop layer filed by its foot point,
 * the bottom centre of its frame in whole pixels, with the gap its frame keeps in the set (0 for a prop not of the
 * set), and every prop with a base kept for stamping: all but those of flat art, empty frames and known cover, which
 * paints no base (a cover with a footprint too). What the manifest says of a frame is read once for each distinct
 * frame. The bitmap starts empty and grows to each dab's box. When the set clears ground cover, every prop of known
 * cover on a layer the caller says may be cleared is recorded for clearing, except one that carries a behaviour or
 * that the caller says to keep (a thing refers to it): that prop stays standing, and is still cover for the gap rule.
 *
 * @param map The map.
 * @param tileSize Tile size in pixels.
 * @param kit The prepared set.
 * @param lookups The editor's frame sizes and footprints.
 * @param clearable Whether a layer's cover may be cleared (the layer is visible and unlocked); none may when left out.
 * @param kept The ids of props that stay standing though they are cover (a thing refers to them); none when left out.
 * @returns The occupancy.
 */
export function createOccupancy(map: MapDocument, tileSize: number, kit: ScatterKit, lookups: FrameLookups, clearable: (layerId: string) => boolean = () => false, kept: ReadonlySet<string> = NO_PROPS): ScatterOccupancy {
  const cellPx = tileSize / kit.cellsPerTile;
  const bucketPx = Math.max(kit.maxGap, cellPx);
  const standing: StandingProp[] = [];
  const occupancy: ScatterOccupancy = {
    tileSize,
    cellsPerTile: kit.cellsPerTile,
    cellPx,
    widthCells: map.width * kit.cellsPerTile,
    heightCells: map.height * kit.cellsPerTile,
    boxLeft: 0,
    boxTop: 0,
    boxWidth: 0,
    boxHeight: 0,
    cells: new Uint16Array(0),
    coverFoot: new Uint16Array(0),
    bucketPx,
    bucketsAcross: Math.ceil((map.width * tileSize) / bucketPx) + 1,
    bucketsDown: Math.ceil((map.height * tileSize) / bucketPx) + 1,
    buckets: new Map(),
    feetX: [],
    feetY: [],
    feetGap: [],
    feetMember: [],
    feetGone: [],
    standing,
    placed: [],
    clears: kit.clearsGroundCover,
    clearFoot: new Int32Array(0),
    clearCol: new Int32Array(0),
    clearRow: new Int32Array(0),
    clearLayer: [],
    clearProp: [],
    clearHead: new Int32Array(0),
    clearNext: new Int32Array(0),
    gone: [],
    scratch: [],
    refusals: new Uint32Array(4),
  };
  const clearFoot: number[] = [];
  const clearCol: number[] = [];
  const clearRow: number[] = [];
  const frames = new Map<string, { readonly flat: boolean; readonly empty: boolean; readonly strip: PropFootprint | undefined }>();
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    const clears = occupancy.clears && clearable(layer.id);
    for (const prop of layer.props) {
      const footprint = lookups.footprintOf(prop);
      const width = lookups.frameSize(prop.frame)?.w ?? footprint?.w ?? 0;
      const left = Math.round(prop.x * tileSize);
      const bottom = Math.round(prop.y * tileSize) + tileSize;
      const key = frameKey(prop.frame);
      const footX = left + Math.floor(width / 2);
      const cover = kit.cover.has(key);
      if (cover && clears && prop.behaviour === undefined && !kept.has(prop.id)) {
        clearFoot.push(occupancy.feetX.length);
        clearCol.push(Math.floor(footX / cellPx));
        clearRow.push(Math.floor((bottom - 1) / cellPx));
        occupancy.clearLayer.push(layer.id);
        occupancy.clearProp.push(prop.id);
      }
      addFoot(occupancy, footX, bottom, kit.gaps.get(key) ?? 0, kit.gaps.has(key) ? 1 : cover ? 2 : 0);
      if (cover) {
        continue;
      }
      let frame = frames.get(key);
      if (frame === undefined) {
        const info = lookups.artOf(prop.frame);
        frame = { flat: isFlat(info), empty: info !== undefined && info.empty === true, strip: artStrip(info) };
        frames.set(key, frame);
      }
      const drawn = footprint !== undefined && footprint.runs.length > 0 ? footprint : undefined;
      if (frame.empty || (frame.flat && drawn === undefined)) {
        continue;
      }
      const base = frame.flat ? drawn : drawn ?? frame.strip;
      standing.push({
        footprint: base,
        solid: !frame.flat,
        x: prop.x,
        y: prop.y,
        flipX: prop.flipX,
        flipY: prop.flipY,
        footX,
        footY: bottom,
        left: Math.min(left, footX) - cellPx,
        top: (base === undefined ? bottom : bottom - base.h) - cellPx,
        right: Math.max(left + (base?.w ?? 0), footX) + cellPx,
        bottom: bottom + cellPx,
      });
    }
  }
  occupancy.clearFoot = Int32Array.from(clearFoot);
  occupancy.clearCol = Int32Array.from(clearCol);
  occupancy.clearRow = Int32Array.from(clearRow);
  occupancy.clearNext = new Int32Array(clearFoot.length);
  return occupancy;
}

/**
 * Adds a placed shape to the bitmaps, or takes it out: one count on each cell of its base, so a base laid over the
 * base of another prop (a set that allows placing on top) leaves that prop's count when it is taken out; a shape of
 * known cover counts its foot cells in `coverFoot` instead.
 *
 * @param occupancy The occupancy.
 * @param placed The placement.
 * @param step 1 to add the base, -1 to take it out.
 */
function paintPlaced(occupancy: ScatterOccupancy, placed: PlacedShape, step: number): void {
  const base = placed.shape.cover ? placed.shape.foot : placed.shape.base;
  const counts = placed.shape.cover ? occupancy.coverFoot : occupancy.cells;
  for (let i = 0; i < base.length; i += 2) {
    const x = placed.kx + (base[i] as number) - occupancy.boxLeft;
    const y = placed.ky + (base[i + 1] as number) - occupancy.boxTop;
    if (x >= 0 && y >= 0 && x < occupancy.boxWidth && y < occupancy.boxHeight) {
      const index = y * occupancy.boxWidth + x;
      counts[index] = (counts[index] as number) + step;
    }
  }
}

/**
 * Grows the bitmap, when it does not already cover them, to hold the cells from `(left, top)` to `(right, bottom)`
 * inclusive: the union of the old box and the new, with `GROW_TILES` of room, on tile edges and on the map, stamped
 * again from the bases of the props standing there and the stroke's placements.
 *
 * @param occupancy The occupancy, changed in place.
 * @param left The box's first column, in cells.
 * @param top The box's first row, in cells.
 * @param right The box's last column, in cells.
 * @param bottom The box's last row, in cells.
 */
export function coverOccupancy(occupancy: ScatterOccupancy, left: number, top: number, right: number, bottom: number): void {
  const per = occupancy.cellsPerTile;
  const l = Math.max(0, left);
  const t = Math.max(0, top);
  const r = Math.min(occupancy.widthCells - 1, right);
  const b = Math.min(occupancy.heightCells - 1, bottom);
  const covered = occupancy.boxWidth > 0 && l >= occupancy.boxLeft && t >= occupancy.boxTop && r < occupancy.boxLeft + occupancy.boxWidth && b < occupancy.boxTop + occupancy.boxHeight;
  if (covered || r < l || b < t) {
    return;
  }
  const had = occupancy.boxWidth > 0;
  const room = GROW_TILES * per;
  const boxLeft = Math.max(0, Math.floor((Math.min(l, had ? occupancy.boxLeft : l) - room) / per) * per);
  const boxTop = Math.max(0, Math.floor((Math.min(t, had ? occupancy.boxTop : t) - room) / per) * per);
  const boxRight = Math.min(occupancy.widthCells, Math.ceil((Math.max(r + 1, had ? occupancy.boxLeft + occupancy.boxWidth : r + 1) + room) / per) * per);
  const boxBottom = Math.min(occupancy.heightCells, Math.ceil((Math.max(b + 1, had ? occupancy.boxTop + occupancy.boxHeight : b + 1) + room) / per) * per);
  occupancy.boxLeft = boxLeft;
  occupancy.boxTop = boxTop;
  occupancy.boxWidth = boxRight - boxLeft;
  occupancy.boxHeight = boxBottom - boxTop;
  const stamped = new Uint8Array(occupancy.boxWidth * occupancy.boxHeight);
  const cell = occupancy.cellPx;
  const tilesAcross = occupancy.boxWidth / per;
  const tilesDown = occupancy.boxHeight / per;
  const originX = boxLeft / per;
  const originY = boxTop / per;
  const placement = { x: 0, y: 0, flipX: false, flipY: false };
  for (const prop of occupancy.standing) {
    if (prop.right < boxLeft * cell || prop.left > boxRight * cell || prop.bottom < boxTop * cell || prop.top > boxBottom * cell) {
      continue;
    }
    placement.x = prop.x - originX;
    placement.y = prop.y - originY;
    placement.flipX = prop.flipX;
    placement.flipY = prop.flipY;
    if (prop.footprint !== undefined) {
      stampFootprint(stamped, tilesAcross, tilesDown, prop.footprint, placement, occupancy.tileSize, per, 1);
    }
    const column = Math.floor(prop.footX / cell) - boxLeft;
    const row = Math.floor((prop.footY - 1) / cell) - boxTop;
    if (prop.solid && column >= 0 && row >= 0 && column < occupancy.boxWidth && row < occupancy.boxHeight) {
      stamped[row * occupancy.boxWidth + column] = 1;
    }
  }
  occupancy.cells = Uint16Array.from(stamped);
  occupancy.coverFoot = new Uint16Array(stamped.length);
  for (const placed of occupancy.placed) {
    paintPlaced(occupancy, placed, 1);
  }
  if (occupancy.clears) {
    occupancy.clearHead = new Int32Array(stamped.length).fill(-1);
    for (let i = occupancy.clearFoot.length - 1; i >= 0; i -= 1) {
      const x = (occupancy.clearCol[i] as number) - boxLeft;
      const y = (occupancy.clearRow[i] as number) - boxTop;
      if (x >= 0 && y >= 0 && x < occupancy.boxWidth && y < occupancy.boxHeight) {
        const cellIndex = y * occupancy.boxWidth + x;
        occupancy.clearNext[i] = occupancy.clearHead[cellIndex] as number;
        occupancy.clearHead[cellIndex] = i;
      } else {
        occupancy.clearNext[i] = -1;
      }
    }
  }
}

/**
 * Whether a cell is in the base of a prop on the map or of a placement of the stroke. A cell outside the bitmap's
 * box reads as free: every dab grows the box over the cells its candidates can reach, so only cells off the map fall
 * outside it, and the map's edge is tested by the art.
 *
 * @param occupancy The occupancy.
 * @param x The cell's column.
 * @param y The cell's row.
 * @returns True when a base covers it.
 */
export function baseAt(occupancy: ScatterOccupancy, x: number, y: number): boolean {
  const bx = x - occupancy.boxLeft;
  const by = y - occupancy.boxTop;
  return bx >= 0 && by >= 0 && bx < occupancy.boxWidth && by < occupancy.boxHeight && occupancy.cells[by * occupancy.boxWidth + bx] !== 0;
}

/**
 * Whether a cell holds the foot cell of a prop of cover the stroke has placed, which a solid placement keeps off. A
 * cell outside the bitmap's box reads as free, as for `baseAt`.
 *
 * @param occupancy The occupancy.
 * @param x The cell's column.
 * @param y The cell's row.
 * @returns True when a placed cover prop has its foot cell there.
 */
export function coverAt(occupancy: ScatterOccupancy, x: number, y: number): boolean {
  const bx = x - occupancy.boxLeft;
  const by = y - occupancy.boxTop;
  return bx >= 0 && by >= 0 && bx < occupancy.boxWidth && by < occupancy.boxHeight && occupancy.coverFoot[by * occupancy.boxWidth + bx] !== 0;
}

/**
 * Whether a foot point keeps its pair gap, `(gap + other) / 2`, from the foot points filed, compared in whole
 * pixels squared: `4 * d^2 >= (gap + other)^2`. The gap is the room a set's props keep among themselves (ADR-123's
 * amendment of 2026-10-13, A1): a foot that is not a prop of the set is skipped unless the set keeps its gap from
 * others, and then counts as a gap of 0; a foot of known cover that is not of the set is skipped either way (A2.3).
 *
 * @param occupancy The occupancy.
 * @param x The foot point's x in pixels.
 * @param y The foot point's y in pixels.
 * @param gap The gap its prop keeps.
 * @param keepsGapFromOthers Whether feet that are not the set's are kept at the gap too.
 * @returns True when no foot point is too close.
 */
export function gapClear(occupancy: ScatterOccupancy, x: number, y: number, gap: number, keepsGapFromOthers: boolean): boolean {
  const bx = Math.min(occupancy.bucketsAcross - 1, Math.max(0, Math.floor(x / occupancy.bucketPx)));
  const by = Math.min(occupancy.bucketsDown - 1, Math.max(0, Math.floor(y / occupancy.bucketPx)));
  for (let row = by - 1; row <= by + 1; row += 1) {
    if (row < 0 || row >= occupancy.bucketsDown) {
      continue;
    }
    for (let column = bx - 1; column <= bx + 1; column += 1) {
      const bucket = column < 0 || column >= occupancy.bucketsAcross ? undefined : occupancy.buckets.get(row * occupancy.bucketsAcross + column);
      if (bucket === undefined) {
        continue;
      }
      for (const index of bucket) {
        const member = occupancy.feetMember[index];
        if (member === 2 || (member === 0 && !keepsGapFromOthers) || occupancy.feetGone[index] === 1) {
          continue;
        }
        const pair = gap + (occupancy.feetGap[index] as number);
        if (pair === 0) {
          continue;
        }
        const dx = x - (occupancy.feetX[index] as number);
        const dy = y - (occupancy.feetY[index] as number);
        if (4 * (dx * dx + dy * dy) < pair * pair) {
          return false;
        }
      }
    }
  }
  return true;
}

/**
 * How many of the set's props have their foot points inside a circle, the stroke's placements among them.
 *
 * @param occupancy The occupancy.
 * @param x The centre's x in pixels.
 * @param y The centre's y in pixels.
 * @param radius The radius in pixels.
 * @returns The count.
 */
export function membersInCircle(occupancy: ScatterOccupancy, x: number, y: number, radius: number): number {
  let count = 0;
  const limit = radius * radius;
  for (let i = 0; i < occupancy.feetX.length; i += 1) {
    if (occupancy.feetMember[i] === 1 && occupancy.feetGone[i] === 0) {
      const dx = (occupancy.feetX[i] as number) - x;
      const dy = (occupancy.feetY[i] as number) - y;
      if (dx * dx + dy * dy <= limit) {
        count += 1;
      }
    }
  }
  return count;
}

/**
 * Finds the known cover a placement would clear: the cover of the document, not yet cleared, whose foot cell is one of
 * the cells of the shape's base (ADR-123's amendment of 2026-10-13, A3.2 and A3.5). The records are left in
 * `occupancy.scratch`, in the order of the base's cells and of each cell's list, and nothing is changed, so a
 * candidate that is then refused costs nothing. The dab grows the box over every cell a candidate's base can reach
 * before it tries one.
 *
 * @param occupancy The occupancy.
 * @param shape The placed entry's shape.
 * @param kx The lead's laying corner column, in cells.
 * @param ky The lead's laying corner row, in cells.
 * @returns How many records are in `occupancy.scratch`.
 */
export function coverUnder(occupancy: ScatterOccupancy, shape: ScatterShape, kx: number, ky: number): number {
  const scratch = occupancy.scratch;
  scratch.length = 0;
  if (!occupancy.clears) {
    return 0;
  }
  const base = shape.base;
  for (let i = 0; i < base.length; i += 2) {
    const x = kx + (base[i] as number) - occupancy.boxLeft;
    const y = ky + (base[i + 1] as number) - occupancy.boxTop;
    if (x < 0 || y < 0 || x >= occupancy.boxWidth || y >= occupancy.boxHeight) {
      continue;
    }
    for (let record = occupancy.clearHead[y * occupancy.boxWidth + x] as number; record !== -1; record = occupancy.clearNext[record] as number) {
      if (occupancy.feetGone[occupancy.clearFoot[record] as number] === 0) {
        scratch.push(record);
      }
    }
  }
  return scratch.length;
}

/**
 * Clears the cover `coverUnder` found: each is flagged gone, so the gap and the count of the set's props no longer see
 * it, and pushed on the stack a restore unwinds.
 *
 * @param occupancy The occupancy.
 * @param out Receives what is cleared, as layer and prop ids, in the order found.
 */
export function clearCollected(occupancy: ScatterOccupancy, out: PropRef[]): void {
  for (const record of occupancy.scratch) {
    occupancy.feetGone[occupancy.clearFoot[record] as number] = 1;
    occupancy.gone.push(record);
    out.push({ layerId: occupancy.clearLayer[record] as string, propId: occupancy.clearProp[record] as string });
  }
  occupancy.scratch.length = 0;
}

/**
 * Enters a placement: its base in the bitmap and each piece's foot point with its gap, as a prop of the set.
 *
 * @param occupancy The occupancy.
 * @param shape The placed entry's shape.
 * @param kx The lead's laying corner column, in cells.
 * @param ky The lead's laying corner row, in cells.
 * @param left The anchor's left edge in pixels.
 * @param top The anchor's top edge in pixels.
 */
export function occupy(occupancy: ScatterOccupancy, shape: ScatterShape, kx: number, ky: number, left: number, top: number): void {
  const placed: PlacedShape = { shape, kx, ky };
  occupancy.placed.push(placed);
  paintPlaced(occupancy, placed, 1);
  for (let i = 0; i < shape.gaps.length; i += 1) {
    addFoot(occupancy, left + (shape.pieces[i * 6 + 4] as number), top + (shape.pieces[i * 6 + 5] as number), shape.gaps[i] as number, 1);
  }
}

/**
 * Where an occupancy stands now, to put it back after a preview.
 *
 * @param occupancy The occupancy.
 * @returns The mark.
 */
export function markOccupancy(occupancy: ScatterOccupancy): OccupancyMark {
  return { feet: occupancy.feetX.length, placed: occupancy.placed.length, gone: occupancy.gone.length };
}

/**
 * Puts an occupancy back to a mark, taking out every placement entered since: its base counts taken off the bitmap,
 * which leaves the counts of any prop it was laid over, its foot points unfiled, last first, and the cover it cleared put
 * back. A box grown since is kept.
 *
 * @param occupancy The occupancy, changed in place.
 * @param mark The mark.
 */
export function restoreOccupancy(occupancy: ScatterOccupancy, mark: OccupancyMark): void {
  while (occupancy.gone.length > mark.gone) {
    occupancy.feetGone[occupancy.clearFoot[occupancy.gone.pop() as number] as number] = 0;
  }
  while (occupancy.placed.length > mark.placed) {
    paintPlaced(occupancy, occupancy.placed.pop() as PlacedShape, -1);
  }
  while (occupancy.feetX.length > mark.feet) {
    const index = occupancy.feetX.length - 1;
    occupancy.buckets.get(bucketKey(occupancy, occupancy.feetX[index] as number, occupancy.feetY[index] as number))?.pop();
    occupancy.feetX.pop();
    occupancy.feetY.pop();
    occupancy.feetGap.pop();
    occupancy.feetMember.pop();
    occupancy.feetGone.pop();
  }
}
